<script setup lang="ts">
  import { computed } from 'vue'
  import { Badge } from '@/components/ui/badge'
  import { ArrowLeftRight, Loader2 } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import ActaportDarkIcon from '@/components/icons/ActaportDarkIcon.vue'
  import JupusIcon from '@/components/icons/JupusIcon.vue'
  import { PlatformSyncStatusEnum } from '@/types/generated/types.gen'

  const IconType = {
    JUPUS: 'jupus',
    ACTAPORT: 'actaport',
    BOTH: 'both',
    REFRESH: 'refresh',
    UNKNOWN: 'unknown'
  } as const

  interface Props {
    status?: string | null
  }

  const props = withDefaults(defineProps<Props>(), {
    status: null
  })

  const { t } = useTranslation({
    en: {
      onlyInJupus: 'Only in Jupus',
      onlyInActaport: 'Only in Actaport',
      inBoth: 'In Both',
      inBothUpdatedInJupus: 'In Both · Updated in Jupus',
      syncing: 'Syncing...',
      unknown: 'Unknown'
    },
    de: {
      onlyInJupus: 'Nur in Jupus',
      onlyInActaport: 'Nur in Actaport',
      inBoth: 'In Beiden',
      inBothUpdatedInJupus: 'In Beiden · Aktualisiert in Jupus',
      syncing: 'Wird synchronisiert...',
      unknown: 'Unbekannt'
    }
  })

  const statusConfig = computed(() => {
    switch (props.status) {
      case PlatformSyncStatusEnum.ONLY_JUPUS:
        return {
          text: t('onlyInJupus'),
          class: 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-100',
          icon: IconType.JUPUS,
          iconClass: 'text-purple-600'
        }
      case PlatformSyncStatusEnum.ONLY_ACTAPORT:
        return {
          text: t('onlyInActaport'),
          class: 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-100',
          icon: IconType.ACTAPORT,
          iconClass: 'text-blue-600'
        }
      case PlatformSyncStatusEnum.IN_BOTH:
        return {
          text: t('inBoth'),
          class: 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-100',
          icon: IconType.BOTH,
          iconClass: 'text-emerald-600'
        }
      case PlatformSyncStatusEnum.IN_BOTH_UPDATED_IN_JUPUS:
        return {
          text: t('inBothUpdatedInJupus'),
          class: 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-100',
          icon: IconType.BOTH,
          iconClass: 'text-amber-600'
        }
      case PlatformSyncStatusEnum.SYNCING:
        return {
          text: t('syncing'),
          class: 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-100',
          icon: IconType.REFRESH,
          iconClass: 'text-amber-600'
        }
      default:
        return {
          text: t('unknown'),
          class: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100',
          icon: IconType.UNKNOWN,
          iconClass: 'text-gray-600'
        }
    }
  })
</script>

<template>
  <Badge
    v-if="status"
    variant="outline"
    :class="statusConfig.class"
    class="inline-flex h-[32px] min-h-[24px] min-w-[24px] items-center gap-1 whitespace-nowrap py-[4px]
      pl-[4px] pr-2 font-medium"
  >
    <span
      v-if="statusConfig.icon === IconType.JUPUS"
      class="flex h-6 items-center gap-1 rounded-full bg-white px-2"
    >
      <JupusIcon
        :class="statusConfig.iconClass"
        class="h-6 w-[9px] flex-shrink-0"
      />
    </span>

    <span
      v-else-if="statusConfig.icon === IconType.BOTH"
      class="flex h-6 items-center gap-1 rounded-full bg-white px-2"
    >
      <JupusIcon
        :class="statusConfig.iconClass"
        class="h-6 w-[9px] flex-shrink-0"
      />
      <ArrowLeftRight class="h-3 w-3 flex-shrink-0 text-black" />
      <ActaportDarkIcon class="h-4 w-4 flex-shrink-0" />
    </span>

    <span
      v-else-if="statusConfig.icon === IconType.ACTAPORT"
      class="text-xs font-semibold"
    >
      <ActaportDarkIcon class="h-4 w-4" />
    </span>

    <span
      v-else-if="statusConfig.icon === IconType.REFRESH"
      class="flex h-6 w-6 items-center justify-center rounded-full bg-white"
    >
      <Loader2
        :class="statusConfig.iconClass"
        class="h-4 w-4 animate-spin"
      />
    </span>

    {{ statusConfig.text }}
  </Badge>
  <span
    v-else
    class="text-sm text-gray-400"
  >
    -
  </span>
</template>
